import { useMemo, useState } from 'react';
import type { OpportunityPnl } from '../types';
import { StatCard } from './StatCard';
import { GlobalDropdown } from './GlobalDropdown';
import { formatDateLong, formatEuro } from '../utils/formatters';
import {
  computePnlYears,
  monthLabel,
  PNL_SECTIONS,
  pnlYearPlans,
  type PnlComputedMonth,
  type PnlRowDef,
} from '../utils/opportunityPnl';
import styles from './OpportunityPnlPanel.module.css';

type Props = {
  pnl: OpportunityPnl;
};

const wholeEuroFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const percentFmt = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function signClass(value: number): string {
  if (value > 0) return styles.pos!;
  if (value < 0) return styles.neg!;
  return styles.zero!;
}

function signedValueNode(value: number) {
  return <span className={signClass(value)}>{formatEuro(value, false)}</span>;
}

function MoneyCell({ row, data }: { row: PnlRowDef; data: PnlComputedMonth }) {
  const raw = data[row.key as keyof PnlComputedMonth] as number;
  // Cost lines are stored positive but shown as deductions, BWA-style.
  const displayed = row.expense && raw !== 0 ? -raw : raw;
  return <span className={signClass(displayed)}>{wholeEuroFmt.format(displayed)}</span>;
}

function PercentCell({ row, data }: { row: PnlRowDef; data: PnlComputedMonth }) {
  const value = data[row.key as keyof PnlComputedMonth] as number | null;
  if (value === null || !Number.isFinite(value)) {
    return <span className={styles.zero}>—</span>;
  }
  const withPlus = row.key === 'revenueGrowthPct' && value > 0;
  return (
    <span className={signClass(value)}>
      {withPlus ? '+' : ''}
      {percentFmt.format(value)} %
    </span>
  );
}

export function OpportunityPnlPanel({ pnl }: Props) {
  const plans = useMemo(() => pnlYearPlans(pnl), [pnl]);
  const computedYears = useMemo(() => computePnlYears(plans), [plans]);
  const [selectedYear, setSelectedYear] = useState(String(computedYears[0]?.year ?? 1));

  const activeYear = useMemo(
    () => computedYears.find((year) => String(year.year) === selectedYear) ?? computedYears[0],
    [computedYears, selectedYear]
  );

  if (!computedYears.length || !activeYear) {
    return (
      <p className="muted">
        No monthly P&L data yet. Regenerate the model to produce a month-by-month projection.
      </p>
    );
  }

  const yearOptions = computedYears.map((year) => ({
    value: String(year.year),
    label: `Year ${year.year}`,
  }));

  const totals = activeYear.totals;
  const columnCount = activeYear.months.length + 2;
  const breakEvenInYear =
    pnl.breakEvenMonth != null
      ? Math.ceil(pnl.breakEvenMonth / 12)
      : null;

  return (
    <div className={styles.root} aria-label="Monthly profit and loss">
      <p className={`muted ${styles.meta}`}>
        Generated {formatDateLong(pnl.generatedAt)} · {pnl.model} · {pnl.currency}
      </p>

      <section className={styles.card} aria-labelledby="pnl-summary-heading">
        <h3 id="pnl-summary-heading" className={styles.cardTitle}>
          Executive summary
        </h3>
        <p className={styles.summary}>{pnl.summary}</p>
      </section>

      <section className={styles.card} aria-labelledby="pnl-year-snapshot-heading">
        <div className={styles.cardHeader}>
          <div>
            <h3 id="pnl-year-snapshot-heading" className={styles.cardTitle}>
              Year {activeYear.year} snapshot
            </h3>
            <p className={styles.cardSubtitle}>Annual totals for the selected plan year</p>
          </div>
          <GlobalDropdown
            label="Projection year"
            value={selectedYear}
            onChange={setSelectedYear}
            options={yearOptions}
          />
        </div>

        <div className={styles.kpiGrid}>
          <StatCard label="Revenue" value={formatEuro(totals.revenue, false)} emoji="📈" />
          <StatCard
            label="Gross profit"
            value={signedValueNode(totals.grossProfit)}
            emoji="💰"
          />
          <StatCard label="EBITDA" value={signedValueNode(totals.ebitda)} emoji="⚡" />
          <StatCard
            label="Net income"
            value={signedValueNode(totals.netIncome)}
            emoji="🏁"
            emphasis={totals.netIncome !== 0}
          />
          <StatCard
            label="Cumulative EBITDA"
            value={signedValueNode(totals.cumulativeEbitda)}
            emoji="🏦"
          />
        </div>

        {pnl.breakEvenMonth ? (
          <p className={styles.breakEvenBadge}>
            Break-even around month {pnl.breakEvenMonth} of the 36-month plan
            {breakEvenInYear ? ` (year ${breakEvenInYear})` : ''}
          </p>
        ) : null}
      </section>

      <section className={styles.card} aria-labelledby="pnl-monthly-heading">
        <div className={`${styles.cardHeader} ${styles.cardHeaderTight}`}>
          <div>
            <h3 id="pnl-monthly-heading" className={styles.cardTitle}>
              Monthly P&L · Year {activeYear.year}
            </h3>
            <p className={styles.cardSubtitle}>
              BWA-style statement — costs shown as deductions, cumulative EBITDA carries over from
              prior plan years
            </p>
          </div>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.tableScroll}>
            <table className={`data-table data-table--compact ${styles.pnlTable}`}>
              <thead>
                <tr>
                  <th className={styles.lineCol}>Line item</th>
                  {activeYear.months.map((m) => (
                    <th key={m.month} className={styles.monthCol}>
                      {monthLabel(m.month)}
                    </th>
                  ))}
                  <th className={styles.totalCol}>Year total</th>
                </tr>
              </thead>
              <tbody>
                {PNL_SECTIONS.map((section) => (
                  <SectionRows
                    key={section.key}
                    title={section.title}
                    rows={section.rows}
                    months={activeYear.months}
                    totals={totals}
                    columnCount={columnCount}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {(pnl.revenueDrivers.length > 0 || pnl.costDrivers.length > 0) && (
        <div className={styles.twoCol}>
          {pnl.revenueDrivers.length > 0 ? (
            <section className={styles.card} aria-labelledby="pnl-revenue-drivers">
              <h3 id="pnl-revenue-drivers" className={styles.cardTitle}>
                Revenue drivers
              </h3>
              <ul className={styles.bulletList}>
                {pnl.revenueDrivers.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {pnl.costDrivers.length > 0 ? (
            <section className={styles.card} aria-labelledby="pnl-cost-drivers">
              <h3 id="pnl-cost-drivers" className={styles.cardTitle}>
                Cost drivers
              </h3>
              <ul className={styles.bulletList}>
                {pnl.costDrivers.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}

      {pnl.keyAssumptions.length > 0 ? (
        <section className={styles.card} aria-labelledby="pnl-assumptions">
          <h3 id="pnl-assumptions" className={styles.cardTitle}>
            Key assumptions
          </h3>
          <p className={styles.cardSubtitle}>Uncertainties and modeling choices baked into this projection</p>
          <ul className={styles.bulletList}>
            {pnl.keyAssumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function SectionRows({
  title,
  rows,
  months,
  totals,
  columnCount,
}: {
  title?: string;
  rows: PnlRowDef[];
  months: PnlComputedMonth[];
  totals: PnlComputedMonth;
  columnCount: number;
}) {
  return (
    <>
      {title ? (
        <tr className={styles.sectionRow}>
          <th scope="rowgroup" className={`${styles.lineCol} ${styles.sectionCell}`}>
            {title}
          </th>
          <td colSpan={columnCount - 1} aria-hidden="true" />
        </tr>
      ) : null}
      {rows.map((row) => {
        const rowClass =
          row.emphasis === 'total'
            ? styles.rowTotal
            : row.emphasis === 'subtotal'
              ? styles.rowSubtotal
              : row.kind === 'percent'
                ? styles.rowPercent
                : undefined;

        return (
          <tr key={row.key} className={rowClass}>
            <th scope="row" className={`${styles.lineCol}${row.indent ? ` ${styles.indent}` : ''}`}>
              {row.label}
            </th>
            {months.map((m) => (
              <td key={m.month} className={styles.monthCol}>
                {row.kind === 'money' ? (
                  <MoneyCell row={row} data={m} />
                ) : (
                  <PercentCell row={row} data={m} />
                )}
              </td>
            ))}
            <td className={styles.totalCol}>
              {row.kind === 'money' ? (
                <MoneyCell row={row} data={totals} />
              ) : (
                <PercentCell row={row} data={totals} />
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}
